import { inject } from '@angular/core';
import { Store } from '@ngxs/store';
import type { AbstractDtoService } from '@pct-ng/core/dto';
import type { ListParams, PageInfo } from '@pct-ng/feature-table-material';
import {
  AbstractTableDataSource,
  ChangeFilterAction,
  type Sort,
  type TablePage,
  TableStateSelectors,
} from '@pct-ng/feature-table-material';
import type { BasePaginationDTO } from '@pct-sdk/core';
import type { Observable } from 'rxjs';
import { BehaviorSubject, combineLatest, startWith, switchMap, tap } from 'rxjs';
import { debounceTime, finalize, map } from 'rxjs/operators';

export class CustomServiceTableDataSource<
  T,
  FILTER_T extends object = any,
> extends AbstractTableDataSource<T> {
  readonly #store = inject(Store);
  readonly #defaultPageInfo: PageInfo = { page: 1, size: 10 };

  readonly #service: AbstractDtoService<
    any,
    any,
    any,
    T,
    any,
    ListParams<T, FILTER_T>,
    BasePaginationDTO
  >;
  readonly #loading$ = new BehaviorSubject<boolean>(false);
  readonly #reloader = new BehaviorSubject<void>(undefined);

  constructor(
    service: AbstractDtoService<any, any, any, T, any, ListParams<T, FILTER_T>, BasePaginationDTO>,
    tableId: string,
    filter$: Observable<FILTER_T>,
  ) {
    super();
    this.#service = service;
    filter$.subscribe(value => this.#store.dispatch(new ChangeFilterAction({ tableId, value })));
  }

  override get isLoading$(): Observable<boolean> {
    return this.#loading$.asObservable();
  }

  reload(): void {
    this.#reloader.next(undefined);
  }

  protected getFilter$(tableId: string): Observable<FILTER_T | undefined> {
    return this.store.select(TableStateSelectors.getFilters<T>(tableId)) as Observable<
      FILTER_T | undefined
    >;
  }

  override getData$(tableId: string): Observable<TablePage<T>> {
    return combineLatest([
      this.getPagination$(tableId),
      this.getSort$(tableId),
      this.getFilter$(tableId),
      this.#reloader.asObservable(),
    ]).pipe(
      startWith<[PageInfo, Sort<T> | undefined, FILTER_T | undefined, void]>([
        this.#defaultPageInfo,
        this.#getDefaultSort(),
        undefined,
        undefined,
      ]),
      tap(() => {
        this.#loading$.next(true);
      }),
      // TODO find better solution:
      debounceTime(100),
      // TODO: Mate: This should be used but when it's connected, the loader gets stuck during column display
      // deepDistinctUntilChanged(),
      switchMap(([pagination, sort, filter]) =>
        // @todo: Insert sort param when it is stable.
        this.#service
          .getAll({
            pagination,
            sort,
            parameters: filter,
          })
          .pipe(
            map(
              (data): TablePage<T> => ({
                data: data.data ?? [],
                pagination: { ...pagination, totalCount: data.pagination.totalCount },
                sort,
              }),
            ),
            finalize(() => {
              this.#loading$.next(false);
            }),
          ),
      ),
    );
  }

  // @todo: Talk about to make DefaultSort and Sort compatible
  #getDefaultSort(): Sort<T> {
    const { sort: field, direction } = this.#service.defaultSort;
    return {
      field,
      order: direction === 'ASC' ? 'asc' : 'desc',
    };
  }
}
